import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { documentsApi } from "@/api/documents";
import { foldersApi } from "@/api/folders";
import { ApiError } from "@/api/client";
import { FolderPicker } from "@/components/dms/FolderPicker";
import type {
  FolderPickerSelection,
  FolderPickerTreeSource,
} from "@/components/dms/FolderPicker";
import type { DocumentDto } from "@/types/api";

interface Props {
  open: boolean;
  onClose: () => void;
  document: DocumentDto | null;
  /**
   * Root of the owning record's folder tree. This is the picker's DEFAULT
   * anchor, NOT a boundary — the dialog opens on the document's own record so
   * the common same-record move costs exactly the clicks it always did, and a
   * search pick re-anchors the tree to whichever record owns the hit. The
   * server permits any destination the caller can access, in any record.
   */
  treeRootId: string | null;
  /**
   * Display name of the record `treeRootId` belongs to (its breadcrumb root),
   * used to name the record a cross-record move would take the document out of
   * and to label the "back to this record" control.
   */
  treeRootName: string;
  onMoved: (updated: DocumentDto) => void;
}

/**
 * Destination picker for moving a single document. The shared FolderPicker is
 * anchored to the document's own record and additionally searchable across
 * every record, so a cross-record move is one search away while the same-record
 * path is unchanged. A cross-record destination is called out in the footer but
 * never blocked (owner decision Q2 — warn, don't gate).
 *
 * <p>Owner identity is read from the ANCHOR, never from `document.entityType` /
 * `entityId`: those are projected from the document's IMMEDIATE folder, and
 * `DocumentFolder.EntityType`/`EntityId` are populated on the tree ROOT only —
 * so for any document in a subfolder (the common case) both are null.</p>
 */
export function MoveDocumentModal({
  open,
  onClose,
  document: doc,
  treeRootId,
  treeRootName,
  onMoved,
}: Props) {
  const { toast } = useToast();
  const [targetId, setTargetId] = useState<string | null>(null);

  // The document's own record — where the picker starts, and where the "back"
  // control returns to.
  const homeAnchor = useMemo<FolderPickerTreeSource | null>(
    () => (treeRootId ? { kind: "folder", rootFolderId: treeRootId } : null),
    [treeRootId],
  );
  const [anchor, setAnchor] = useState<FolderPickerTreeSource | null>(homeAnchor);

  // Clear any prior selection AND re-anchor to the document's own record each
  // time the modal is opened, so a previous cross-record browse never leaks
  // into the next document's move.
  useEffect(() => {
    if (!open) return;
    setTargetId(null);
    setAnchor(homeAnchor);
  }, [open, homeAnchor]);

  // Folder search returns record ROOTS only, so a pick of this record's own
  // root resets the anchor below rather than re-anchoring to an identical
  // tree. An `entity` anchor therefore always names a DIFFERENT record.
  const crossRecord = anchor?.kind === "entity" && targetId !== null;

  // Name the destination's record the same way FolderContents names the
  // current one — off the breadcrumb root, which is root-first.
  const destination = useQuery({
    queryKey: destinationRecordKey(targetId),
    queryFn: () => foldersApi.breadcrumb(targetId!),
    enabled: crossRecord,
    retry: false,
  });
  const destinationRecordName = destination.data?.[0]?.name ?? null;

  const pickFolder = (folderId: string, folder?: FolderPickerSelection) => {
    setTargetId(folderId);

    // Picking this record's own root (from search, or the tree's own root row)
    // is the way home: restore the default anchor instead of re-anchoring to
    // the same tree under a different `kind`, which would read as cross-record.
    if (folderId === treeRootId) {
      setAnchor(homeAnchor);
      return;
    }

    // Owner metadata rides root folders only; a subfolder pick carries none and
    // correctly leaves the current anchor where it is.
    if (!folder?.entityType || !folder?.entityId) return;
    const sameAnchor =
      anchor?.kind === "entity" &&
      anchor.entityType === folder.entityType &&
      anchor.entityId === folder.entityId;
    if (sameAnchor) return;
    setAnchor({
      kind: "entity",
      entityType: folder.entityType,
      entityId: folder.entityId,
    });
  };

  const move = useMutation({
    mutationFn: (folderId: string) => documentsApi.move(doc!.id, folderId),
    onSuccess: (updated) => {
      toast({
        message: `Moved “${updated.title}” to ${updated.folderName ?? "the folder"}.`,
        tone: "success",
      });
      onMoved(updated);
      onClose();
    },
    onError: (err) => {
      const message =
        err instanceof ApiError
          ? // A destination the caller can't see is reported as not-found so a
            // restricted folder's existence isn't disclosed; the envelope's
            // generic "requested resource" copy reads badly against a folder
            // the user just picked, so name what actually happened.
            err.status === 404
            ? "That destination folder is no longer available. It may have been archived or removed, or you may not have access to it."
            : err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not move the document.";
      toast({ message, tone: "error" });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move document"
      description={
        doc ? `Choose a destination folder for “${doc.title}”.` : undefined
      }
      size="md"
      footer={
        <div className="flex w-full flex-col gap-space-2">
          {crossRecord && (
            <p className="m-0 text-caption text-gray-mid">
              Moves this document out of {treeRootName} and into{" "}
              {destinationRecordName ?? "another record"}.
            </p>
          )}
          <div className="flex items-center justify-end gap-space-3">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!targetId || move.isPending}
              loading={move.isPending}
              onClick={() => targetId && move.mutate(targetId)}
            >
              Move here
            </Button>
          </div>
        </div>
      }
    >
      {/* The picker renders unconditionally: search reaches every record, so
          even a document whose own tree root can't be resolved has a way to a
          destination rather than the old dead-end message. */}
      <div className="flex flex-col gap-space-2">
        {anchor?.kind === "entity" && (
          <div>
            <Button
              variant="text"
              size="sm"
              type="button"
              onClick={() => {
                setAnchor(homeAnchor);
                setTargetId(null);
              }}
            >
              ← Back to {treeRootName}
            </Button>
          </div>
        )}
        <FolderPicker
          treeSource={anchor}
          value={targetId}
          onChange={pickFolder}
          search={{ placeholder: "Search every record by name…" }}
          disabledFolderId={doc?.folderId ?? null}
        />
      </div>
    </Modal>
  );
}

/**
 * Co-located query-key factory (the stale-after-save convention: no inline
 * `queryKey` array literals outside a key factory). Reads the destination
 * folder's breadcrumb purely to name its owning record in the cross-record
 * warning; nothing mutates under this key.
 */
function destinationRecordKey(folderId: string | null) {
  return ["dms", "moveDestinationRecord", folderId] as const;
}
